import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import tinycolor from 'tinycolor2';

const ANIMATION_TIME = 250;

type FlipProps = {
  children: string;
  accentColor: string;
};

export function Flip({ children, accentColor }: FlipProps) {
  const valueRef = useRef<HTMLSpanElement | null>(null);
  const firstHalfRef = useRef<HTMLSpanElement | null>(null);
  const flipTopRef = useRef<HTMLSpanElement | null>(null);
  const flipBottomRef = useRef<HTMLSpanElement | null>(null);

  const darkenAccentColor = tinycolor(accentColor).isDark()
    ? tinycolor(accentColor).brighten(65).toRgbString()
    : tinycolor(accentColor).darken(35).toRgbString();

  useEffect(() => {
    const { current: valueElement } = valueRef;
    const { current: firstHalfElement } = firstHalfRef;
    const { current: flipTopElement } = flipTopRef;
    const { current: flipBottomElement } = flipBottomRef;

    if (
      !valueElement ||
      !firstHalfElement ||
      !flipTopElement ||
      !flipBottomElement
    ) {
      return;
    }

    if (!valueElement.textContent) {
      valueElement.textContent = children;
      return;
    }

    const previousValue = valueElement.textContent;

    firstHalfElement.classList.add('flip');
    flipTopElement.classList.add('flip');
    flipBottomElement.classList.add('flip');

    valueElement.textContent = previousValue;
    firstHalfElement.textContent = children;

    flipTopElement.textContent = previousValue;
    flipBottomElement.textContent = children;

    const flipTopHandler = () => {
      if (!flipTopElement) {
        return;
      }

      flipTopElement.textContent = children;
      flipTopElement.classList.remove('flip');
    };

    const flipBottomHandler = () => {
      if (!valueElement || !firstHalfElement || !flipBottomElement) {
        return;
      }

      valueElement.textContent = children;
      firstHalfElement.classList.remove('flip');
      flipBottomElement.classList.remove('flip');
    };

    flipTopElement.addEventListener('animationend', flipTopHandler);
    flipBottomElement.addEventListener('animationend', flipBottomHandler);

    return () => {
      if (!flipTopElement || !flipBottomElement) {
        return;
      }

      flipTopElement.removeEventListener('animationend', flipTopHandler);
      flipBottomElement.removeEventListener('animationend', flipBottomHandler);
    };
  }, [children]);

  return (
    <>
      <Value
        ref={valueRef}
        _accentColor={accentColor}
        _darkenAccentColor={darkenAccentColor}
      />
      <FirstHalfValue
        ref={firstHalfRef}
        _accentColor={accentColor}
        _darkenAccentColor={darkenAccentColor}
      />

      <FlipTop
        ref={flipTopRef}
        _accentColor={accentColor}
        _darkenAccentColor={darkenAccentColor}
      />
      <FlipBottom
        ref={flipBottomRef}
        _accentColor={accentColor}
        _darkenAccentColor={darkenAccentColor}
      />
    </>
  );
}

interface ValueProps {
  _accentColor: string;
  _darkenAccentColor: string;
}

const Value = styled.span.attrs<ValueProps>({
  className: createReadableClass({ component: 'flip', element: 'value' })
})`
  display: block;
  width: 100%;
  overflow: hidden;
  background-color: inherit;
`;

const flipBackground = ({
  _accentColor,
  _darkenAccentColor
}: ValueProps) => keyframes`
  from {
    background-color: ${_darkenAccentColor};
  }
  to {
    background-color: ${_accentColor};
  }
`;

const FirstHalfValue = styled(Value).attrs({
  className: createReadableClass({
    component: 'flip',
    element: 'first-half-value'
  })
})`
  display: none;
  height: 50%;
  position: absolute;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  animation: ${flipBackground} ${ANIMATION_TIME * 2}ms ease-in;

  &.flip {
    display: block;
  }
`;

const flipTop = ({ _accentColor, _darkenAccentColor }: ValueProps) => keyframes`
    from {
      background-color: ${_accentColor};
    }

    to {
      transform: rotateX(-90deg);
      background-color: ${_darkenAccentColor};
    }
  `;

const FlipTop = styled(FirstHalfValue).attrs({
  className: createReadableClass({
    component: 'flip',
    element: 'top'
  })
})`
  transform-origin: left bottom;
  animation: ${flipTop} ${ANIMATION_TIME}ms ease-in both;

  &.flip {
    display: block;
  }
`;

const flipBottom = ({
  _accentColor,
  _darkenAccentColor
}: ValueProps) => keyframes`
    from {
      background-color: ${_darkenAccentColor};
    }

    to {
      transform: rotateX(0deg);
      background-color: ${_accentColor};
    }
  `;

const FlipBottom = styled(Value).attrs({
  className: createReadableClass({
    component: 'flip',
    element: 'bottom'
  })
})`
  display: none;
  align-items: flex-end;
  justify-content: center;
  height: 50%;
  position: absolute;
  bottom: 0;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  animation: ${flipBottom} ${ANIMATION_TIME}ms ease-out ${ANIMATION_TIME}ms both;

  &.flip {
    display: flex;
    transform-origin: top;
    transform: rotateX(90deg);
  }
`;
